import { useCallback, useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import {
  $transferRequestsApprovedListStore,
  $transferRequestsGridListStore,
  $transferRequestsLimitsStore,
  $transferUpdateStatusStore,
} from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { type TransferRequestResponse, TransferRequestStatusEnum } from '@shared/api/rest/transferRequests';
import { TransferTable } from './transferTable';
import { ConfirmModal, OTPModal, TransferRequestConfirmModal } from '@widgets';
import { Text } from '@shared/widgets';
import ConfirmIcon from '@assets/icons/confirm.svg';

interface TransferRequestsProps {
  className?: string;
}

export const TransferRequests = (props: TransferRequestsProps) => {
  const { navigate } = useRouteHook();

  const transferStore = useUnit($transferRequestsGridListStore.store);
  const transferRequestLimit = useUnit($transferRequestsLimitsStore.store);

  const [selectedTransfer, setSelectedTransfer] = useState<TransferRequestResponse | null>(null);
  const [showOtpModal, setShowOtpModal] = useState<TransferRequestResponse | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<TransferRequestResponse | null>(null);
  const paymentsRequestsState = useListState({ list: 'paymentsRequests' });

  const { t } = useTranslation('payments');

  useEffect(() => {
    $transferRequestsLimitsStore.events.update();
  }, []);

  useEffect(() => {
    $transferRequestsGridListStore.events.update({ Count: paymentsRequestsState.count, FromIndex: paymentsRequestsState.fromIndex });
  }, [paymentsRequestsState]);

  const updateGridsData = useCallback(() => {
    paymentsRequestsState.onChangePage(1);
    $transferRequestsGridListStore.events.update({ Count: paymentsRequestsState.count, FromIndex: 0 });
    $transferRequestsApprovedListStore.events.update({ status: TransferRequestStatusEnum.WaitingForApproval, Count: 5, FromIndex: 0 });
  }, [paymentsRequestsState.onChangePage]);

  const submitHandler = useCallback(async () => {
    await $transferUpdateStatusStore.events.update({ id: selectedTransfer!.id, status: TransferRequestStatusEnum.WaitingForApproval });
    await $transferRequestsGridListStore.events.update({ Count: paymentsRequestsState.count, FromIndex: paymentsRequestsState.fromIndex });
    await $transferRequestsApprovedListStore.events.update({ status: TransferRequestStatusEnum.WaitingForApproval, Count: 5, FromIndex: 0 });
    setSelectedTransfer(null);
  }, [selectedTransfer?.id, paymentsRequestsState.count, paymentsRequestsState.fromIndex]);

  return (
    <div className={cn('rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black', props.className)}>
      <Text className='mb-6' weight='semibold' size={PaletteUtils.isInsigniaPalette ? '2xl' : 'lg'}>
        {t('transferRequest.title')}
      </Text>
      <TransferTable
        isLoading={transferStore.loading}
        data={transferStore.data.data}
        totalItemCount={transferStore.data.total}
        currentPage={paymentsRequestsState.page}
        countItemPage={paymentsRequestsState.count}
        onChangePage={paymentsRequestsState.onChangePage}
        onChangeCountItemPage={paymentsRequestsState.onChangeCount}
        setSelectedTransfer={setSelectedTransfer}
      />
      {selectedTransfer && (
        <TransferRequestConfirmModal
          isOpen={!!selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
          transactionId={selectedTransfer.id}
          transferData={selectedTransfer}
          onOpenOtpModal={
            selectedTransfer.status !== TransferRequestStatusEnum.Draft ? (data) => (setShowOtpModal(data), setSelectedTransfer(null)) : undefined
          }
          cancelLabel={
            selectedTransfer.status === TransferRequestStatusEnum.WaitingForApproval
              ? t('modals.transferRequestConfirm.buttons.reject')
              : t('buttons.cancel', { ns: 'common' })
          }
          confirmLabel={
            selectedTransfer.status === TransferRequestStatusEnum.Draft
              ? t('buttons.send', { ns: 'common' })
              : t('modals.transferRequestConfirm.buttons.approve')
          }
          extraSubmitHandle={selectedTransfer.status === TransferRequestStatusEnum.Draft ? submitHandler : undefined}
          readOnly={![TransferRequestStatusEnum.Draft, TransferRequestStatusEnum.WaitingForApproval].includes(selectedTransfer.status)}
          noDocumentUpload={![TransferRequestStatusEnum.Draft].includes(selectedTransfer.status)}
          isLimitReached={selectedTransfer.senderAmount >= transferRequestLimit.data.outgoingTransferLimit}
          updateGridsData={updateGridsData}
        />
      )}
      <OTPModal
        isOpen={!!showOtpModal}
        onClose={() => setShowOtpModal(null)}
        data={showOtpModal!}
        onSubmit={(data) => (setShowConfirmModal(data!), updateGridsData())}
      />
      <ConfirmModal
        isOpen={!!showConfirmModal}
        onClose={() => setShowConfirmModal(null)}
        icon={<ConfirmIcon />}
        title={(showConfirmModal?.totalAmount || 0) >= 50000 ? t('notifications.approved.title2') : t('notifications.approved.title')}
        description={
          <div className='flex flex-col gap-2'>
            <p>{t('notifications.approved.subtitle')}</p>
            {(showConfirmModal?.totalAmount || 0) >= 50000 && <p>{t('notifications.approved.description')}</p>}
          </div>
        }
        buttonTitle='Transaction page'
        onClick={() => navigate(RoutePaths.accountTransaction.onNavigate(showConfirmModal!.paymentId, { back: RoutePaths.payments.onNavigate() }))}
      />
    </div>
  );
};
